/**
 * React Hook for Performance Monitoring
 * 
 * Provides easy integration with the performance monitoring service
 * for React components and navigation.
 */

import React, { useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { performanceMonitor } from '../services/performanceMonitor';

/**
 * Hook for tracking route transitions
 */
export const useRoutePerformance = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const previousLocation = useRef<string>('');

  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousLocation.current;

    if (previousPath && previousPath !== currentPath) {
      // End previous route transition
      performanceMonitor.endRouteTransition();
      
      // Start new route transition
      performanceMonitor.startRouteTransition(previousPath, currentPath);
    } else if (!previousPath) {
      // Initial load
      performanceMonitor.startRouteTransition('/', currentPath);
    }

    // Update previous location
    previousLocation.current = currentPath;

    // Mark route as ready after a short delay to allow components to render
    const timer = setTimeout(() => {
      performanceMonitor.endRouteTransition();
    }, 100);

    return () => clearTimeout(timer);
  }, [location.pathname, navigationType]);
};

/**
 * Hook for tracking component render performance
 */
export const useComponentPerformance = (componentName: string, props?: Record<string, any>) => {
  const renderStartTime = useRef<number>(performance.now());
  const hasTracked = useRef<boolean>(false);

  useEffect(() => {
    if (!hasTracked.current) {
      const renderTime = performance.now() - renderStartTime.current;
      performanceMonitor.trackComponentRender(componentName, renderTime, props);
      hasTracked.current = true;
    }
  });

  // Reset tracking on re-render
  useEffect(() => {
    renderStartTime.current = performance.now();
    hasTracked.current = false;
  });
};

/**
 * Hook for tracking API calls with automatic timing
 */
export const useAPIPerformance = () => {
  const trackAPICall = useCallback((
    url: string,
    method: string,
    startTime: number,
    endTime: number,
    status: number,
    size?: number
  ) => {
    performanceMonitor.trackAPICall(url, method, startTime, endTime, status, size);
  }, []);

  return { trackAPICall };
};

/**
 * Hook for custom performance marks and measures
 */
export const usePerformanceMarks = () => {
  const mark = useCallback((name: string, metadata?: Record<string, any>) => {
    performanceMonitor.mark(name, metadata);
  }, []);

  const measure = useCallback((name: string, startMark: string, endMark?: string) => {
    performanceMonitor.measure(name, startMark, endMark);
  }, []);

  const startTiming = useCallback((name: string) => {
    mark(`${name}-start`);
    return () => {
      mark(`${name}-end`);
      measure(name, `${name}-start`, `${name}-end`);
    };
  }, [mark, measure]);

  return { mark, measure, startTiming };
};

/**
 * Hook for accessing performance data
 */
export const usePerformanceData = () => {
  const getSummary = useCallback(() => {
    return performanceMonitor.getSummary();
  }, []);

  const generateReport = useCallback(() => {
    return performanceMonitor.generateReport();
  }, []);

  const exportData = useCallback(() => {
    performanceMonitor.exportData();
  }, []);

  const clear = useCallback(() => {
    performanceMonitor.clear();
  }, []);

  return {
    getSummary,
    generateReport,
    exportData,
    clear
  };
};

/**
 * Hook for monitoring long-running operations
 */
export const useOperationTimer = () => {
  const timers = useRef<Map<string, number>>(new Map());

  const startOperation = useCallback((operationName: string, metadata?: Record<string, any>) => {
    const startTime = performance.now();
    timers.current.set(operationName, startTime);
    performanceMonitor.mark(`operation-${operationName}-start`, metadata);
  }, []);

  const endOperation = useCallback((operationName: string, metadata?: Record<string, any>) => {
    const startTime = timers.current.get(operationName);
    if (startTime) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      performanceMonitor.mark(`operation-${operationName}-end`, metadata);
      performanceMonitor.measure(
        `operation-${operationName}`,
        `operation-${operationName}-start`,
        `operation-${operationName}-end`
      );

      timers.current.delete(operationName);
      
      return duration;
    }
    return 0;
  }, []);

  return { startOperation, endOperation };
};

/**
 * Hook for tracking data loading performance
 */
export const useDataLoadPerformance = () => {
  const trackDataLoad = useCallback((
    dataType: string,
    recordCount: number,
    loadTime: number,
    cacheHit: boolean = false
  ) => {
    performanceMonitor.mark(`data-load-${dataType}`, {
      recordCount,
      loadTime,
      cacheHit,
      recordsPerSecond: recordCount / (loadTime / 1000)
    });
  }, []);

  return { trackDataLoad };
};

/**
 * HOC for automatically tracking component performance
 */
export const withPerformanceTracking = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  componentName?: string
) => {
  const componentDisplayName = componentName || WrappedComponent.displayName || WrappedComponent.name || 'Component';
  
  const PerformanceTrackedComponent = (props: P) => {
    useComponentPerformance(componentDisplayName, props as Record<string, any>);
    return <WrappedComponent {...props} />;
  };

  PerformanceTrackedComponent.displayName = `withPerformanceTracking(${componentDisplayName})`;
  
  return PerformanceTrackedComponent;
};